import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { AppLoader } from '../../Components/common/AppLoader/AppLoader';
import { ArticleCard } from '../../Components/ui/ArticleCard/ArticleCard';
import { fetchDoctors, fetchSpecialties } from '../../api/doctorApi';
import {
  fetchNewsArticleById,
  fetchNewsArticles,
  getArticleIdFromSlug,
  mapArticleToDetailProps,
  mapArticleToHomeCardProps,
} from '../../api/newsArticlesApi';
import { getSpecialtyNames } from '../../utils/doctorHelpers';
import aboutusBg from "../../assets/images/aboutus-bg.png";
import './newsarticalsdetails.css';

const buildDoctorsById = (doctors = []) =>
  Object.fromEntries(doctors.map((doc) => [doc.doctor_id, doc]));

export const Newsarticalsdetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [comment, setComment] = useState('');
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const articleId = getArticleIdFromSlug(slug);

    const load = async () => {
      setLoading(true);
      setError('');
      setArticle(null);
      setRelated([]);

      if (!articleId) {
        setError('Article not found.');
        setLoading(false);
        return;
      }

      try {
        const [articleRow, allRows, doctorRows, specialtyRows] = await Promise.all([
          fetchNewsArticleById(articleId),
          fetchNewsArticles(),
          fetchDoctors(),
          fetchSpecialties(),
        ]);
        if (cancelled) return;

        const doctorsById = buildDoctorsById(doctorRows);
        const specialtyNames = getSpecialtyNames(specialtyRows);
        const detail = mapArticleToDetailProps(articleRow, doctorsById, specialtyNames);
        const allCards = allRows.map((row) =>
          mapArticleToHomeCardProps(row, specialtyNames),
        );

        setArticle(detail);
        setRelated(
          allCards
            .filter((item) => item.id !== detail.id)
            .filter((item) => !detail.category || item.category === detail.category)
            .concat(allCards.filter((item) => item.id !== detail.id && item.category !== detail.category))
            .slice(0, 3),
        );
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load article.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <AppLoader variant="page" />
    );
  }

  if (error || !article) {
    return (
      <div className="nad-not-found">
        <p>{error || 'Article not found.'}</p>
        <Button onClick={() => navigate('/news-articles')}>Back to Articles</Button>
      </div>
    );
  }

  const paragraphs = article.content ? [article.content] : [];

  return (
    <div className="nad-page">
      <section
        className="na-hero"
        style={{ backgroundImage: `url(${aboutusBg})` }}
      >
        <div className="store-page__hero-overlay">
          <Container>
            <p className="store-page__hero-label">News & Articles</p>
            <h1 className="store-page__hero-title">
              Connecting Doctor to trusted care worldwide and seamless support.
            </h1>
          </Container>
        </div>
      </section>

      <section className="nad-body">
        <Container>
          <div className="nad-body__hero-img-wrap">
            <img src={article.image} alt={article.title} className="nad-body__hero-img" />
          </div>

          <h2 className="nad-body__title">{article.title}</h2>

          {paragraphs.map((para, idx) => (
            <div key={idx}>
              <p className="nad-body__para">{para}</p>
              {idx === 0 && article.image && (
                <div className="nad-body__mid-img-wrap">
                  <img src={article.image} alt="article mid" className="nad-body__mid-img" />
                </div>
              )}
            </div>
          ))}
        </Container>
      </section>

      <section className="nad-bottom">
        <Container>
          <Row className="g-5">
            <Col xs={12} lg={7}>
              <h3 className="nad-comments__heading">Add Comments</h3>

              <div className="nad-comment-form">
                <div className="nad-comment-form__avatar-ring">
                  <img src={article.author.avatar} alt="you" className="nad-comment-form__avatar" />
                </div>
                <div className="nad-comment-form__input-wrap">
                  <Form.Control
                    type="text"
                    placeholder="Ask a questions..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="nad-comment-form__input"
                  />
                  <button type="button" className="nad-comment-form__send" aria-label="Send comment">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button>
                </div>
              </div>

              {article.comments.length > 0 && (
                <div className="nad-comments-list">
                  {article.comments.map((c) => (
                    <div key={c.id} className="nad-comment">
                      <img src={c.avatar} alt={c.author} className="nad-comment__avatar" />
                      <div className="nad-comment__body">
                        <div className="nad-comment__header">
                          <span className="nad-comment__author">{c.author}</span>
                          <span className="nad-comment__date">{c.date}</span>
                        </div>
                        <p className="nad-comment__text">{c.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Col>

            <Col xs={12} lg={5}>
              <h3 className="nad-related__heading">You also Like</h3>
              <div className="nad-related-list">
                {related.length > 0 ? (
                  related.map((rel) => (
                    <ArticleCard key={rel.id} {...rel} />
                  ))
                ) : (
                  <p className="nad-related__empty">No related articles yet.</p>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};
